import api, { tokenManager } from './api';
import { AuthTokens, User } from '../types';
import { getSSOLoginUrl } from '../constants/config';

export const authService = {
  /**
   * Redirect to SSO login page (OAuth flow).
   *
   * Uses .replace (not .href = url) so the current in-app URL is REPLACED
   * in browser history instead of stacked under the SSO URL — without that,
   * pressing browser ← from any post-login page lands on the SSO URL and
   * gets 302'd back, making back-navigation feel "stuck".
   */
  redirectToSSO(): void {
    if (typeof window !== 'undefined') {
      window.location.replace(getSSOLoginUrl());
    }
  },

  /**
   * Exchange OAuth authorization code for JWT tokens via backend proxy
   */
  async exchangeCode(code: string): Promise<AuthTokens> {
    const response = await api.post<AuthTokens>('/auth/exchange/', { code });
    await tokenManager.setTokens(response.data);
    return response.data;
  },

  /**
   * Logout - call SSO logout then clear local tokens
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = await tokenManager.getRefreshToken();
      if (refreshToken) {
        await api.post('/auth/logout/', { refresh: refreshToken });
      }
    } catch {
      // Proceed with local cleanup even if SSO logout fails
    }
    await tokenManager.clearTokens();
  },

  /**
   * Get current user profile from SSO via backend proxy
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me/');
    const data = response.data;

    // Build a User object with legacy profile compatibility
    const user: User = {
      id: data.user_id || data.id,
      username: data.username || '',
      email: data.email || '',
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      full_name: data.full_name || `${data.first_name || ''} ${data.last_name || ''}`.trim(),
      is_active: data.is_active !== false,
      is_superuser: data.is_superuser || false,
      user_type: data.user_type || null,
      organization: data.organization || null,
      job_title: data.job_title || '',
      phone_number: data.phone_number || '',
      profile_photo: data.profile_photo || null,
      data_entry_open: data.data_entry_open !== false,
      talent_enabled: data.talent_enabled || false,
      student_active: data.student_active !== false,
      // Legacy profile object for backward compatibility with existing screens
      profile: {
        user_type: data.user_type || 'org_employee',
        organization: data.organization?.id || null,
        organization_name: data.organization?.name || null,
        can_view_all_organizations: data.user_type === 'strategy_team' || data.user_type === 'executive' || data.user_type === 'performance_evaluator' || data.user_type === 'evaluation_approver' || data.is_superuser || false,
        must_change_password: data.must_change_password || false,
        profile_image: data.profile_photo || null,
        user_type_display: data.user_type_display || data.user_type || '',
        data_entry_open: data.data_entry_open !== false,
      },
    };

    return user;
  },

  /**
   * Refresh access token via SSO proxy
   */
  async refreshToken(): Promise<AuthTokens> {
    const refreshToken = await tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<AuthTokens>('/auth/refresh/', {
      refresh: refreshToken,
    });

    await tokenManager.setTokens({
      access: response.data.access,
      refresh: response.data.refresh || refreshToken,
    });

    return response.data;
  },

  /**
   * Verify token validity by calling /auth/me/
   */
  async verifyToken(): Promise<boolean> {
    try {
      const token = await tokenManager.getAccessToken();
      if (!token) return false;

      await api.get('/auth/me/');
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Change password via SSO proxy
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password/', {
      current_password: oldPassword,
      new_password: newPassword,
      confirm_password: newPassword,
    });
  },

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    return tokenManager.hasValidToken();
  },
};

export default authService;
